import Image from 'next/image'
import { Nameplate, Tile, TileCaption, TileImage } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import NameplateImage from 'components/NameplateImage'
import {
  getCourseTime,
  getChaptersCount,
} from 'pages/classes/[slug]/components/utils'
import { Course } from 'types/data'
import { CM_TIMELINE_VIEW_I18N_NAMESPACE } from '../../../i18n'
import { ScrollAnimation } from '../ScrollAnimation'
import { Section } from '../Section'
import { Timeline } from '../Timeline'

export const CourseOverview = () => {
  const { t } = useTranslation(CM_TIMELINE_VIEW_I18N_NAMESPACE)

  return (
    <ScrollAnimation isFadeInAnimation>
      <Section>
        <div className='row justify-content-center'>
          <div className='col-12 col-md-9 col-lg-7'>
            <Timeline
              text={t('variant_1.courseOverview.timeline')}
              top='60px'
              bottom='32px'
            />

            <CourseImage />

            <Details />
          </div>
        </div>
      </Section>
    </ScrollAnimation>
  )
}

const Details = () => {
  const { course } = useStaticData<{ course: Course }>()
  const { t } = useTranslation(CM_TIMELINE_VIEW_I18N_NAMESPACE)

  // Continuous cut
  const hasSubchapters = course.sortedSubchapters?.length
  const chapters = hasSubchapters
    ? course.sortedSubchapters
    : course.sortedChapters

  const courseDuration = getCourseTime(chapters, hasSubchapters)
  const chaptersCount = getChaptersCount(chapters)

  const DETAILS_ITEMS = [
    {
      label: t('variant_1.courseOverview.details.chapters', {
        chaptersCount,
      }),
    },
    {
      label: courseDuration,
    },
    {
      label: t('variant_1.courseOverview.details.atYourOwnPace'),
    },
    {
      label: t('variant_1.courseOverview.details.forEveryone'),
    },
    {
      label: t('variant_1.courseOverview.details.audioFriendly'),
    },
  ]

  return (
    <div className='row no-gutters justify-content-center'>
      {DETAILS_ITEMS.map(({ label }, index) => (
        <div className='col-auto' key={index}>
          <div className='d-flex mc-text-color--tint'>
            <p className='d-none d-md-flex mc-text--1-line mc-text-h6'>
              {index !== 0 && <span className='mc-mx-1'>•</span>}
              {label}
            </p>
            <p className='d-block d-md-none mc-text--1-line mc-text-h8'>
              {index !== 0 && <span className='mc-mx-1'>•</span>}
              {label}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

const CourseImage = () => {
  const { course } = useStaticData<{ course: Course }>()

  return (
    <Tile
      aspectRatio='21x9'
      className='mc-py-10 mc-corners--md mc-overflow--hidden mc-mb-4'
      style={{
        boxShadow: '0 40px 80px 0 rgba(0, 0, 0, 0.25)',
      }}
    >
      <CustomTileOverlay />
      <TileImage
        className='mc-corners--md mc-overflow--hidden'
        image={
          <Image
            alt=''
            src={course.cinematic12x5 ?? ''}
            layout='fill'
            objectFit='cover'
            objectPosition='30% center' // The instructor face is usually centered, so we need to move it to the right
            priority
          />
        }
      />
      <TileCaption position='left center'>
        <div
          className='mc-text--center d-inline-block mc-pl-md-4'
          style={{ maxWidth: '50%' }}
        >
          <Nameplate
            size='small'
            image={
              <NameplateImage
                alt={course.instructorName}
                src={course.nameplate}
              />
            }
          />
          <span className='d-none d-md-block mc-text-large'>
            {course.title}
          </span>
          <span className='d-block d-md-none mc-text-x-small'>
            {course.title}
          </span>
        </div>
      </TileCaption>
    </Tile>
  )
}

const CustomTileOverlay = () => (
  <div
    className='mc-tile__component mc-tile-overlay'
    style={{
      background: `
        linear-gradient(270deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.60) 100%), 
        linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50.1%, rgba(0, 0, 0, 0.50) 100.16%)`,
    }}
  />
)
